import clsx from "clsx";
import { FC, useEffect, useMemo, useState } from "react";
import { useWindowSize } from "react-use";

import { ImageSlider } from "@/features/common/components/imageCarousel/ImageSlider";
import { PostActionButton } from "@/features/post/components/PostActionButton";
import { HoverSubscribeMask } from "@/features/post/components/postCard/components/HoveringMask/HoverSubscribeMask";
import { PostDetailFragment } from "@/generated/generated-hooks";

interface PostCarouselProps {
  postBlurLevel: number;
  userBlurLevel: number;
  isOwner: boolean;
  postDetail: PostDetailFragment;
}

export const PostCarousel: FC<PostCarouselProps> = (props) => {
  const { userBlurLevel, postDetail, postBlurLevel } = props;
  const { title, images } = postDetail;
  const [postContentWidth, setPostContentWidth] = useState(undefined);
  const { width } = useWindowSize();

  useEffect(() => {
    const element = document.querySelector("#post-content");
    if (!element) return;

    setPostContentWidth(element.clientWidth);
  }, [width]);

  //max-w-3xl
  const maxLength = postContentWidth ?? 768;

  const carouselHeight = useMemo(() => {
    const heights = images.map((image) => {
      if (image.intrinsicSize.width > maxLength) {
        const height = Math.floor(
          (maxLength * image.intrinsicSize.height) / image.intrinsicSize.width,
        );
        return height;
      }

      return image.intrinsicSize.height;
    });
    const maxHeight = Math.max(...heights);
    return maxHeight;
  }, [images, maxLength]);

  if (images?.length === 0) return null;

  const isMultipleImages = images.length > 1;

  const carouselItems = images.map((item) => {
    return { imgSrc: item.url, alt: title, hasAlpha: item.hasAlpha };
  });

  const unoptimized = postBlurLevel > 0 && userBlurLevel === 0;

  return (
    <div className="relative" data-test-id="PostCarousel-container">
      <div className="absolute inset-0 z-20 md:rounded-lg overflow-hidden">
        <HoverSubscribeMask post={postDetail} logEventSource="post-carousel" />
      </div>

      <div className="absolute flex top-2 right-2 z-50">
        <PostActionButton
          postDetail={postDetail}
          portalDomNodeSelector="#PostDialog-fullScreenDialogUtilityButtonContainer"
        />
      </div>

      <div
        className={clsx("w-full image-post-detail", isMultipleImages && "mb-7")}
        style={{ height: carouselHeight, maxHeight: maxLength }}
      >
        <ImageSlider items={carouselItems} unoptimized={unoptimized} />
      </div>
    </div>
  );
};
